import { z } from 'zod'

/**
 * The primary filters — Warehouse, Marketplace, Store, Brand, date range, SKU,
 * ASIN — as every data page carries them: in the URL, validated here, and
 * sent to the API in the backend's parameter names.
 *
 * Optional with `.catch` throughout: a query string is user input, and a bad
 * value falls back to its default instead of failing the page. Defaults are
 * applied at read time and pruned on the way out (`pruned`), so a page's
 * clean URL is its default view (rules.md E9).
 *
 * The router parses search values as JSON, so `?storeIds=[1,2]` arrives as
 * an array.
 */

const ids = z.array(z.number().int()).catch([])
const day = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined)
const text = z.string().max(200).optional().catch(undefined)

/** A factory, so a page extends it with its own params: `.extend({ page })`. */
export function primaryFilterSearchSchema() {
  return z.object({
    warehouseIds: ids.describe('Warehouses to include, by id. Empty means every warehouse.'),
    marketplaceIds: ids.describe('Marketplaces to include, by id. Empty means every marketplace.'),
    storeIds: ids.describe('Stores to include, by id. Empty means every store.'),
    brandIds: ids.describe('Brands to include, by id; 0 is Unbranded. Empty means every brand.'),
    dateFrom: day.describe('First day of the period, YYYY-MM-DD, inclusive.'),
    dateTo: day.describe('Last day of the period, YYYY-MM-DD, inclusive.'),
    sku: text.describe('SKU code to narrow to.'),
    asin: text.describe('ASIN to narrow to.'),
  })
}

export type PrimaryFilterValue = z.infer<ReturnType<typeof primaryFilterSearchSchema>>
export type PrimaryFilterKey = keyof PrimaryFilterValue

export const PRIMARY_FILTER_KEYS = [
  'warehouseIds',
  'marketplaceIds',
  'storeIds',
  'brandIds',
  'dateFrom',
  'dateTo',
  'sku',
  'asin',
] as const satisfies readonly PrimaryFilterKey[]

export const EMPTY_PRIMARY_FILTERS: PrimaryFilterValue = {
  warehouseIds: [],
  marketplaceIds: [],
  storeIds: [],
  brandIds: [],
  dateFrom: undefined,
  dateTo: undefined,
  sku: undefined,
  asin: undefined,
}

/** How many filters are set. The date range counts once, however many ends it has. */
export function countActiveFilters(value: Partial<PrimaryFilterValue>): number {
  let count = 0
  if (value.warehouseIds?.length) count++
  if (value.marketplaceIds?.length) count++
  if (value.storeIds?.length) count++
  if (value.brandIds?.length) count++
  if (value.dateFrom || value.dateTo) count++
  if (value.sku) count++
  if (value.asin) count++
  return count
}

/** The backend's parameter names, with `undefined` for anything unset. */
export function toApiParams(value: Partial<PrimaryFilterValue>) {
  const list = (ids: number[] | undefined) => (ids?.length ? ids : undefined)
  const str = (value: string | undefined) => (value?.trim() ? value.trim() : undefined)
  return {
    warehouse_ids: list(value.warehouseIds),
    marketplace_ids: list(value.marketplaceIds),
    store_ids: list(value.storeIds),
    brand_ids: list(value.brandIds),
    date_from: str(value.dateFrom),
    date_to: str(value.dateTo),
    sku: str(value.sku),
    asin: str(value.asin),
  }
}

/** Drop undefined, empty strings, empty arrays and `page: 1`. */
export function pruned<T extends Record<string, unknown>>(search: T): T {
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(search)) {
    if (value === undefined || value === '') continue
    if (Array.isArray(value) && value.length === 0) continue
    if (key === 'page' && value === 1) continue
    out[key] = value
  }
  return out as T
}
